import './styles.css'

import { useSupplyOptionsMetadata } from '@/api/useMetadata/useMasterMetadata'
import type { Pocket } from '@/api/useOrders/requests'
import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Collapse, DatePicker, Skeleton, Switch } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useDestinationsContext } from '../../../../Contexts/DestinationsContext'
import { useOrderContainersContext } from '../../../../Contexts/OrderContainersContext'
import { useSupplyOptionContext } from '../../../../Contexts/SupplyOptionContext'
import { useWizardMaster } from '../../../../Contexts/WizardMasterContext'
import { PaneHeader } from '../Layout/PaneHeader'
import { SupplyFilterSelect } from './components/SupplyFilterSelect'
import {
  DEFAULT_FLAG_STATE,
  DEFAULT_SUPPLY_FILTER_STATE,
  SupplyOwnerDefinition,
  type SupplyOptionFilterOptions,
} from './types'
import { SupplyOwnerCollapse } from './components/SupplyOwnerCollapse'
import { getOptions } from './util'
import { useDefaultSupplyOwnerMap } from '@/components/OrderWizard/Contexts/util'
import { allSupplyOptionsOnOrder } from '../../../DisplayPane/util'

const { Panel } = Collapse

export const SupplyFilters = () => {
  const {
    selectedSort,
    reloadSupplyOptions,
    optionsQueryStatus,
    includeAlternates: use_alternates,
    setIncludeAlternates,
    onlyInNetwork: only_in_network,
    setOnlyInNetwork,
    onlySingleSupplier: single_supplier_per_load,
    setOnlySingleSupplier,
    initialSupplyFilters,
    excludeOutages: exclude_outages,
    setExcludeOutages,
  } = useSupplyOptionContext()

  const { compartments, productGroupsApi } = useOrderContainersContext()
  const { orderDestinations } = useDestinationsContext()
  const { wizardOrder, currentEntryMode } = useWizardMaster()

  const [filters, setFilters] = useState<SupplyOptionFilterOptions>(DEFAULT_SUPPLY_FILTER_STATE)
  const [flags, setFlags] = useState({ only_in_network, use_alternates, single_supplier_per_load, exclude_outages })
  const [selectedSupplyOwners, setSelectedSupplyOwners] = useState<Record<string, SupplyOwnerDefinition>>()

  const isByProduct = currentEntryMode === 'by_product'
  const products = isByProduct
    ? Object.keys(productGroupsApi?.productGroups ?? {})
    : [...new Set<string>(compartments?.flatMap((c: Pocket) => c?.product_name))]
  const { data: metadata } = useSupplyOptionsMetadata(products)

  const supplyDestinations = orderDestinations.map((d) => d.store_number)
  const defaultSelectedSupplyOwners = useMemo(() => {
    const useExistingSupplyOwners = allSupplyOptionsOnOrder(
      wizardOrder,
      compartments,
      productGroupsApi.productGroups,
      isByProduct
    )

    const definitions =
      useExistingSupplyOwners && wizardOrder?.supply_option?.supply_owner_map?.definitions
        ? wizardOrder.supply_option.supply_owner_map.definitions
        : useDefaultSupplyOwnerMap(orderDestinations, compartments, productGroupsApi.productGroups, isByProduct).definitions
    return definitions.reduce(
      (acc, def) => {
        acc[`${def.location_name}::${def.product}`] = def
        return acc
      },
      {} as Record<string, SupplyOwnerDefinition>
    )
  }, [orderDestinations, compartments])

  const getDefaultFilters: () => SupplyOptionFilterOptions = () => ({
    ...DEFAULT_SUPPLY_FILTER_STATE,
    ...initialSupplyFilters,
    as_of: wizardOrder?.estimated_load_date ? dayjs(wizardOrder.estimated_load_date) : dayjs(),
  })

  useEffect(() => {
    setFilters(getDefaultFilters())
    setSelectedSupplyOwners(defaultSelectedSupplyOwners)
  }, [wizardOrder?.estimated_load_date, defaultSelectedSupplyOwners])

  function onResetFilters() {
    setFilters(getDefaultFilters())
    setSelectedSupplyOwners(defaultSelectedSupplyOwners)
    setFlags(DEFAULT_FLAG_STATE)
    setOnlyInNetwork(false)
    setIncludeAlternates(false)
    setOnlySingleSupplier(false)
    setExcludeOutages(true)
  }

  function onApplyFilters() {
    reloadSupplyOptions(
      wizardOrder,
      isByProduct ? productGroupsApi.destinations : supplyDestinations,
      isByProduct ? productGroupsApi.compartments : compartments,
      selectedSort,
      true,
      filters,
      flags,
      selectedSupplyOwners ? { definitions: Object.values(selectedSupplyOwners) } : undefined,
      productGroupsApi.productGroups,
      isByProduct
    )
    setOnlyInNetwork(flags.only_in_network)
    setIncludeAlternates(flags.use_alternates)
    setOnlySingleSupplier(flags.single_supplier_per_load)
    setExcludeOutages(flags.exclude_outages)
  }

  return (
    <>
      <PaneHeader />
      <Vertical justifyContent='space-between' height={'auto'}>
        <Vertical height={'auto'} scroll>
          <Collapse className='m-2' size='small' defaultActiveKey={['1', '2']} expandIconPosition='end'>
            <Panel header='Supply' key='1' className='supply-filters-panel'>
              <Skeleton active loading={optionsQueryStatus === 'fetching'} paragraph={{ rows: 16 }}>
                <Vertical gap='1em'>
                  <Vertical>
                    <Texto>Price Effective</Texto>
                    <DatePicker
                      showTime
                      format='M/D/YY h:mm A'
                      value={filters.as_of}
                      onChange={(value) => setFilters({ ...filters, as_of: value })}
                    />
                  </Vertical>
                  <SupplyFilterSelect
                    title='Terminal'
                    mode='multiple'
                    options={getOptions(metadata, 'terminals')}
                    value={filters.terminals}
                    onChange={(value) => {
                      setFilters({ ...filters, terminals: value })
                    }}
                    right={
                      <Horizontal verticalCenter gap='4px'>
                        <Texto>In Network</Texto>
                        <Switch
                          size='small'
                          value={!!flags.only_in_network}
                          onChange={(value) => setFlags({ ...flags, only_in_network: value })}
                        />
                      </Horizontal>
                    }
                  />
                  <SupplyFilterSelect
                    title='Supplier'
                    value={filters.suppliers}
                    options={getOptions(metadata, 'suppliers')}
                    onChange={(value) => {
                      setFilters({ ...filters, suppliers: value })
                    }}
                    mode='multiple'
                    right={
                      <Horizontal verticalCenter gap='4px'>
                        <Texto>Single Per Load</Texto>
                        <Switch
                          size='small'
                          value={!!flags.single_supplier_per_load}
                          onChange={(value) => setFlags({ ...flags, single_supplier_per_load: value })}
                        />
                      </Horizontal>
                    }
                  />
                  <SupplyFilterSelect
                    title='Price Type'
                    mode='multiple'
                    options={getOptions(metadata, 'price_types')}
                    onChange={(value) => {
                      setFilters({ ...filters, price_types: value })
                    }}
                    value={filters.price_types}
                  />
                  <SupplyFilterSelect
                    mode='multiple'
                    title='Load Number'
                    value={filters.load_numbers}
                    onChange={(value) => {
                      setFilters({ ...filters, load_numbers: value })
                    }}
                    options={getOptions(metadata, 'load_numbers')}
                  />
                  <SupplyFilterSelect
                    mode='multiple'
                    title='Contract Number'
                    value={filters.contracts}
                    onChange={(value) => {
                      setFilters({ ...filters, contracts: value })
                    }}
                    options={getOptions(metadata, 'contracts')}
                  />
                  <SupplyFilterSelect
                    hideSelect={!flags.use_alternates}
                    mode='multiple'
                    title='Alternates'
                    value={filters.alternate_products}
                    right={
                      <Horizontal verticalCenter gap='4px'>
                        <Texto>Include</Texto>
                        <Switch
                          size='small'
                          value={!!flags.use_alternates}
                          onChange={(value) => setFlags({ ...flags, use_alternates: value })}
                        />
                      </Horizontal>
                    }
                    options={getOptions(metadata, 'alternates')}
                    onChange={(value) => {
                      setFilters({ ...filters, alternate_products: value })
                    }}
                  />
                  <Horizontal justifyContent='space-between' alignItems='center'>
                    <Texto>Outages</Texto>
                    <Horizontal alignItems='center' gap='4px'>
                      <Texto>Exclude</Texto>
                      <Switch
                        size='small'
                        value={!!flags.exclude_outages}
                        onChange={(value) => setFlags({ ...flags, exclude_outages: value })}
                      />
                    </Horizontal>
                  </Horizontal>
                </Vertical>
              </Skeleton>
            </Panel>
            <Panel header='Supply Owners' key='2' className='supply-filters-panel'>
              <SupplyOwnerCollapse
                metadata={metadata}
                selectedSupplyOwners={selectedSupplyOwners}
                setSelectedSupplyOwners={setSelectedSupplyOwners}
                productGroups={productGroupsApi.productGroups}
                isByProduct={isByProduct}
              />
            </Panel>
          </Collapse>
        </Vertical>
        <Horizontal
          className='py-3 px-4'
          justifyContent='space-between'
          style={{ overflow: 'visible', borderTop: '1px solid var(--bg-2)' }}
        >
          <GraviButton className='reset-filters-button' appearance='link' onClick={onResetFilters}>
            Reset Filters
          </GraviButton>
          <GraviButton onClick={onApplyFilters} color='primary'>
            Apply Filters
          </GraviButton>
        </Horizontal>
      </Vertical>
    </>
  )
}
